import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard, Coins, ShieldCheck, Lock } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { api, money, formatApiErrorDetail, track, resolveImg } from "../lib/api";
import { toast } from "sonner";

export default function Checkout() {
  const { items, subtotal } = useCart();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", name: "", address: "", city: "", state: "", zip_code: "" });
  const [couponInput, setCouponInput] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [payment, setPayment] = useState("stripe");
  const [placing, setPlacing] = useState(false);
  const [settings, setSettings] = useState({ free_shipping_threshold: 75, flat_shipping: 7.95 });

  useEffect(() => {
    api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {});
    const savedCoupon = sessionStorage.getItem("shodan_coupon");
    if (savedCoupon) setCouponInput(savedCoupon);
    track("checkout_initiated", sessionStorage.getItem("shodan_source") || "website", {});
  }, []);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, email: f.email || user.email, name: f.name || user.name || "" }));
  }, [user]);

  const discount = coupon ? coupon.discount : 0;
  const memberDiscount = user?.club_member && !coupon ? Math.round(subtotal * 0.05 * 100) / 100 : 0;
  const cryptoDiscount = payment === "usdc" ? Math.round((subtotal - discount - memberDiscount) * 0.02 * 100) / 100 : 0;
  const shipping = subtotal >= settings.free_shipping_threshold ? 0 : settings.flat_shipping;
  const total = Math.round((subtotal - discount - memberDiscount - cryptoDiscount + shipping) * 100) / 100;

  const applyCoupon = async () => {
    try {
      const { data } = await api.post("/coupons/validate", { code: couponInput, subtotal });
      setCoupon(data);
      toast.success(`Coupon applied: ${data.description}`);
    } catch (err) {
      setCoupon(null);
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const placeOrder = async (e) => {
    e.preventDefault();
    if (items.length === 0) { toast.error("Your cart is empty"); return; }
    setPlacing(true);
    try {
      const { data } = await api.post("/orders", {
        ...form,
        items: items.map((i) => ({ item_type: i.item_type, slug: i.slug, name: i.name, price: i.price, quantity: i.quantity, image: i.image, sku_code: i.sku_code || null, variant_label: i.variant_label || null, component_skus: i.component_skus || null })),
        coupon_code: coupon?.code,
        payment_method: payment,
        source: sessionStorage.getItem("shodan_source") || "website",
      });
      if (payment === "stripe") {
        const res = await api.post("/payments/checkout", { order_id: data.order_id, origin_url: window.location.origin });
        window.location.href = res.data.checkout_url;
        return;
      }
      sessionStorage.removeItem("shodan_coupon");
      navigate(`/order/${data.order_id}?t=${data.access_token}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setPlacing(false);
    }
  };

  const input = (key, label, props = {}) => (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-widest text-black/50">{label}</label>
      <input data-testid={`checkout-${key}`} required value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="w-full border border-black/20 px-4 py-3 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-[#4A5D23]" {...props} />
    </div>
  );

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-28 text-center">
        <h1 className="font-heading text-3xl uppercase font-bold tracking-tight">Your Cart Is Empty</h1>
        <p className="text-black/50 mt-2 text-sm">Add some gear before checking out.</p>
        <a data-testid="empty-cart-shop-link" href="/shop" className="inline-block mt-6 bg-[#121212] text-white px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-[#4A5D23] transition-colors duration-150">Shop All Gear</a>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-10">
      <h1 className="font-heading text-3xl md:text-4xl uppercase font-bold tracking-tight mb-2">Checkout</h1>
      <p className="text-xs uppercase tracking-widest font-bold text-black/40 flex items-center gap-2 mb-8"><Lock size={12} /> Secure Checkout — powered by Stripe</p>
      <form onSubmit={placeOrder} className="grid grid-cols-1 lg:grid-cols-5 gap-10">
        <div className="lg:col-span-3 space-y-8">
          <div className="border border-black/10 p-6">
            <h3 className="font-heading font-bold uppercase tracking-tight mb-4">Contact & Shipping</h3>
            {!user && <p className="text-xs text-black/50 mb-4">Checking out as a guest. <span className="text-[#4A5D23]">Have an account? Sign in from the menu.</span></p>}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {input("email", "Email", { type: "email", onBlur: (e) => { const v = e.target.value.trim().toLowerCase(); if (v.includes("@")) localStorage.setItem("shodan_email", v); } })}
              {input("name", "Full Name")}
            </div>
            <div className="mt-4">{input("address", "Street Address")}</div>
            <div className="grid grid-cols-3 gap-4 mt-4">
              {input("city", "City")}
              {input("state", "State")}
              {input("zip_code", "ZIP Code")}
            </div>
          </div>
          <div className="border border-black/10 p-6">
            <h3 className="font-heading font-bold uppercase tracking-tight mb-4">Payment Method</h3>
            <div className="space-y-3">
              <label data-testid="payment-card" className={`flex items-start gap-3 border p-4 cursor-pointer transition-colors duration-150 ${payment === "stripe" ? "border-[#4A5D23] bg-[#4A5D23]/5" : "border-black/15"}`}>
                <input type="radio" name="payment" checked={payment === "stripe"} onChange={() => setPayment("stripe")} className="mt-1 accent-[#4A5D23]" />
                <CreditCard size={20} className="text-[#4A5D23] mt-0.5" />
                <div>
                  <p className="text-sm font-bold uppercase tracking-wider">Card / Apple Pay / Google Pay</p>
                  <p className="text-xs text-black/50 mt-0.5">Secure checkout powered by Stripe. You'll be redirected to complete payment.</p>
                </div>
              </label>
              <label data-testid="payment-crypto" className={`flex items-start gap-3 border p-4 cursor-pointer transition-colors duration-150 ${payment === "usdc" ? "border-[#4A5D23] bg-[#4A5D23]/5" : "border-black/15"}`}>
                <input type="radio" name="payment" checked={payment === "usdc"} onChange={() => setPayment("usdc")} className="mt-1 accent-[#4A5D23]" />
                <Coins size={20} className="text-[#4A5D23] mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-bold uppercase tracking-wider">USDC on Base <span className="ml-2 bg-[#4A5D23] text-white text-[10px] px-2 py-0.5 tracking-widest">2% OFF</span></p>
                  <p className="text-xs text-black/50 mt-0.5">Send USDC on the Base network from any wallet. A 2% crypto discount is applied automatically.</p>
                </div>
              </label>
            </div>
          </div>
        </div>
        <div className="lg:col-span-2">
          <div className="border border-black/10 p-6 bg-[#F4F3F0] sticky top-28">
            <h3 className="font-heading font-bold uppercase tracking-tight mb-4">Order Summary</h3>
            <div className="space-y-3 max-h-56 overflow-y-auto">
              {items.map((i) => (
                <div key={`${i.item_type}-${i.slug}-${i.sku_code || ""}`} className="flex items-center gap-3 text-sm">
                  <img src={resolveImg(i.image)} alt={i.name} className="w-12 h-12 object-cover border border-black/5" />
                  <div className="flex-1 min-w-0">
                    <p className="truncate font-semibold">{i.name}</p>
                    {i.variant_label && <p className="text-[11px] text-[#4A5D23] font-semibold">{i.variant_label}{i.sku_code ? ` · SKU ${i.sku_code}` : ""}</p>}
                    <p className="text-xs text-black/50">Qty {i.quantity}</p>
                  </div>
                  <span className="font-bold">{money(i.price * i.quantity)}</span>
                </div>
              ))}
            </div>
            <div className="flex gap-0 mt-5">
              <input data-testid="coupon-input" value={couponInput} onChange={(e) => setCouponInput(e.target.value)} placeholder="Coupon code"
                className="flex-1 border border-black/20 px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#4A5D23]" />
              <button data-testid="apply-coupon-btn" type="button" onClick={applyCoupon} className="bg-[#121212] text-white px-4 text-xs font-bold uppercase tracking-widest hover:bg-[#4A5D23] transition-colors duration-150">Apply</button>
            </div>
            <div className="mt-5 space-y-2 text-sm border-t border-black/10 pt-4">
              {authLoading ? (
                <p className="text-black/30 animate-pulse text-xs py-4">Loading your pricing…</p>
              ) : (<>
              <div className="flex justify-between"><span className="text-black/60">Subtotal</span><span>{money(subtotal)}</span></div>
              {discount > 0 && <div className="flex justify-between text-[#4A5D23] font-semibold"><span>Discount ({coupon.code})</span><span data-testid="checkout-discount">-{money(discount)}</span></div>}
              {memberDiscount > 0 && <div className="flex justify-between text-[#4A5D23] font-semibold"><span>Club Member (5%)</span><span data-testid="checkout-member-discount">-{money(memberDiscount)}</span></div>}
              {cryptoDiscount > 0 && <div className="flex justify-between text-[#4A5D23] font-semibold"><span>Crypto Discount (2%)</span><span data-testid="checkout-crypto-discount">-{money(cryptoDiscount)}</span></div>}
              <div className="flex justify-between"><span className="text-black/60">Shipping</span><span>{shipping === 0 ? "FREE" : money(shipping)}</span></div>
              <div className="flex justify-between font-bold text-lg border-t border-black/10 pt-3"><span>Total</span><span data-testid="checkout-total">{money(total)}</span></div>
              </>)}
            </div>
            <button data-testid="place-order-btn" type="submit" disabled={placing || items.length === 0}
              className="w-full mt-6 bg-[#4A5D23] text-white py-4 text-sm font-bold uppercase tracking-widest hover:bg-[#3D4D1D] transition-colors duration-150 disabled:opacity-50">
              {placing ? "Placing Order…" : `Place Order — ${money(total)}`}
            </button>
            <p className="text-[11px] text-black/40 mt-3 flex items-center gap-1.5"><ShieldCheck size={13} /> 30-day easy returns · Secure checkout · Fast US shipping</p>
          </div>
        </div>
      </form>
    </div>
  );
}
